import {CommonModule, CurrencyPipe} from '@angular/common';
import {Component, EventEmitter, Inject, Input, LOCALE_ID, Output} from '@angular/core';
import {AmountPipe, TranslationService} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  Coverage,
  deepEqual,
  Dropdown,
  Duration,
  getDisabilityRiderDurations,
  getDisabilityRiderMaxCoverage,
  getPersonInsuranceTypeFromDuration,
  HUMANIA_EMMA_V2_CANCER_RIDER_COVERAGE_AMOUNT_INTERVAL_EMMA_PRO,
  HUMANIA_EMMA_V2_CANCER_RIDER_MAX_AGE,
  HUMANIA_EMMA_V2_CANCER_RIDER_MAX_COVERAGE,
  HUMANIA_EMMA_V2_CANCER_RIDER_MIN_COVERAGE,
  HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES,
  HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
  HUMANIA_EMMA_V2_DISABILITY_RIDER_COVERAGE_AMOUNT_INTERVAL,
  HUMANIA_EMMA_V2_DISABILITY_RIDER_MAX_AGE,
  HUMANIA_EMMA_V2_DISABILITY_RIDER_MIN_COVERAGE,
  HUMANIA_EMMA_V2_MAX_COVERAGE,
  HUMANIA_EMMA_V2_MAX_TERM_AGE,
  HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
  HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
  HUMANIA_EMMA_V2_TERM_LIFE_DURATIONS,
  HUMANIA_EMMA_V2_WHOLE_LIFE_DURATIONS,
  isWholeLife,
  JobRiskClass,
  PersonInsuranceType,
  Protection,
  ProviderId,
  RiderType,
  Sex,
  Version,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import cloneDeep from 'lodash/cloneDeep';
import {DividerModule} from 'primeng/divider';
import {DialogService} from 'primeng/dynamicdialog';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  filter,
  first,
  forkJoin,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {ProtectionService} from '../../services/protection.service';
import {RenewalPricesComponent} from '../proposal-page/coverage-builder-section/life-insurance/term-life/renewal-prices/renewal-prices.component';
import {ProductSelectorCardComponent} from './product-selector-card/product-selector-card.component';
import {createProtectionBuilderObservablesAndSubjects} from './protection-builder-observables';

const DEFAULT_SHOWN_RENEWAL_DURATIONS = [Duration.T10, Duration.T20, Duration.T25];

@UntilDestroy()
@Component({
  selector: 'em-protection-builder',
  imports: [CommonModule, ProductSelectorCardComponent, DividerModule, ProgressSpinnerModule, AmountPipe],
  templateUrl: './protection-builder.component.html',
})
export class ProtectionBuilderComponent {
  @Input({required: true}) ratesVersion!: Version;

  @Input({required: true}) set age(age: number) {
    this.ageSubject.next(age);
  }

  @Input({required: true}) set sex(sex: Sex) {
    this.sexSubject.next(sex);
  }

  @Input({required: true}) set smoker(smoker: boolean) {
    this.smokerSubject.next(smoker);
  }

  @Input({required: true}) set categoryCode(categoryCode: CategoryCode) {
    this.categoryCodeSubject.next(categoryCode);
  }

  @Input() set jobRiskClass(jobRiskClass: JobRiskClass | undefined) {
    this.jobRiskClassSubject.next(jobRiskClass || null);
  }

  @Input() set prefilledCoverage(coverage: Coverage | null) {
    if (coverage && this.isPrefillEnabled) {
      this.prefilledCoverageSubject.next(coverage);
      this.isPrefillEnabled = false;
    }
  }

  @Input() set surchargeRate(surchargeRate: number | undefined) {
    this.surchargeRateSubject.next(surchargeRate || null);
  }

  @Input() blocked = false;

  @Input() withToasterAlerts = false;

  @Output() protectionBuilt = new EventEmitter<Protection | undefined>();

  private readonly observables = createProtectionBuilderObservablesAndSubjects();
  private readonly builtProtectionSubject = this.observables.builtProtectionSubject;
  private readonly coverageSubject = this.observables.coverageSubject;
  private readonly ageSubject = this.observables.ageSubject;
  private readonly sexSubject = this.observables.sexSubject;
  private readonly smokerSubject = this.observables.smokerSubject;
  private readonly categoryCodeSubject = this.observables.categoryCodeSubject;
  private readonly jobRiskClassSubject = this.observables.jobRiskClassSubject;
  private readonly surchargeRateSubject = this.observables.surchargeRateSubject;
  private readonly termLifeCoverageAmountSubject = this.observables.termLifeCoverageAmountSubject;
  private readonly termLifeCoverageAmountOptionsSubject =
    this.observables.termLifeCoverageAmountOptionsSubject;
  private readonly termLifeDurationOptionsSubject = this.observables.termLifeDurationOptionsSubject;
  private readonly wholeLifeCoverageAmountSubject = this.observables.wholeLifeCoverageAmountSubject;
  private readonly wholeLifeCoverageAmountOptionsSubject =
    this.observables.wholeLifeCoverageAmountOptionsSubject;
  private readonly wholeLifeDurationOptionsSubject = this.observables.wholeLifeDurationOptionsSubject;
  private readonly childRiderCoverageAmountOptionsSubject =
    this.observables.childRiderCoverageAmountOptionsSubject;
  private readonly disabilityRiderCoverageAmountOptionsSubject =
    this.observables.disabilityRiderCoverageAmountOptionsSubject;
  private readonly disabilityRiderDurationOptionsSubject =
    this.observables.disabilityRiderDurationOptionsSubject;
  private readonly cancerRiderCoverageAmountOptionsSubject =
    this.observables.cancerRiderCoverageAmountOptionsSubject;

  readonly totalCoverage$ = this.observables.totalCoverage$;
  readonly policyFee$ = this.observables.policyFee$;
  readonly totalPrice$ = this.observables.totalPrice$;
  readonly termLifeInputs$ = this.observables.termLifeInputs$;
  readonly wholeLifeInputs$ = this.observables.wholeLifeInputs$;
  readonly childRiderInputs$ = this.observables.childRiderInputs$;
  readonly disabilityRiderInputs$ = this.observables.disabilityRiderInputs$;
  readonly cancerRiderInputs$ = this.observables.cancerRiderInputs$;
  readonly isFr: boolean;
  readonly riderType = RiderType;
  readonly providerId = ProviderId.HUMANIA_EMMA_V2;
  readonly defaultChildRiderCoverageOptions: Dropdown<number>[];
  readonly currencyPipe: CurrencyPipe;

  readonly PRODUCT_TYPE_TO_OPTIONS: Partial<
    Record<
      PersonInsuranceType | RiderType,
      {
        coverageOptions: BehaviorSubject<Dropdown<number>[]>;
        durationOptions?: BehaviorSubject<Dropdown<Duration>[]> | BehaviorSubject<Dropdown<number>[]>;
      }
    >
  > = {
    WHOLE: {
      coverageOptions: this.wholeLifeCoverageAmountOptionsSubject,
      durationOptions: this.wholeLifeDurationOptionsSubject,
    },
    TERM: {
      coverageOptions: this.termLifeCoverageAmountOptionsSubject,
      durationOptions: this.termLifeDurationOptionsSubject,
    },
    [RiderType.CHILD]: {coverageOptions: this.childRiderCoverageAmountOptionsSubject},
    [RiderType.DISABILITY]: {
      coverageOptions: this.disabilityRiderCoverageAmountOptionsSubject,
      durationOptions: this.disabilityRiderDurationOptionsSubject,
    },
    [RiderType.CANCER]: {coverageOptions: this.cancerRiderCoverageAmountOptionsSubject},
  };

  isPriceLoading = false;
  prefilledCoverageSubject = new Subject<Coverage>();
  isPrefillEnabled = true;

  constructor(
    private readonly protectionService: ProtectionService,
    private readonly dialogService: DialogService,
    readonly translate: TranslationService,
    @Inject(LOCALE_ID) locale: string,
  ) {
    this.currencyPipe = new CurrencyPipe(locale);
    this.isFr = translate.currentLanguageIsFr();
    this.defaultChildRiderCoverageOptions = HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES.map((value) => ({
      name: this.currencyPipe.transform(Number(value), 'CAD', 'symbol-narrow', '1.0-0') ?? '',
      value: value,
    }));

    this.setProtectionBuilderListener();
    this.setCoverageAmountOptionsListeners();
    this.setDurationOptionsListeners();
    this.setPrefilledCoverageListener();
  }

  onProductSelectorInputsAltered(
    selectedProduct: {coverageAmount: number; duration?: Duration | number},
    type: PersonInsuranceType | RiderType,
  ) {
    if (type === 'WHOLE' || type === 'TERM') {
      this.onCoverageAmountAltered(selectedProduct.coverageAmount, type);

      if (selectedProduct.duration && typeof selectedProduct.duration === 'string') {
        this.onLifeInsuranceProductSelected(selectedProduct.coverageAmount, selectedProduct.duration);
      }
    } else {
      if (typeof selectedProduct.duration === 'string') {
        throw new Error('Duration must be a number');
      }
      this.onRiderAltered(type, selectedProduct.coverageAmount, selectedProduct.duration);
    }
  }

  onRenewalPricesPopupRequested() {
    const allPossibleCoverages = HUMANIA_EMMA_V2_TERM_LIFE_DURATIONS.map((duration) => ({
      lifeInsurances: {[duration]: this.termLifeCoverageAmountSubject.value},
    }));
    const duration = (Object.keys(this.coverageSubject.value?.lifeInsurances ?? {}) as Duration[]).find(
      (key) => !isWholeLife(key),
    );
    const hasWholeLife = Object.keys(this.coverageSubject.value?.lifeInsurances ?? {}).some((key) =>
      isWholeLife(key),
    );

    this.dialogService.open(RenewalPricesComponent, {
      closable: true,
      closeOnEscape: true,
      width: '80%',
      data: {
        ratesVersion: this.ratesVersion,
        coverages: allPossibleCoverages,
        providerId: ProviderId.HUMANIA_EMMA_V2,
        categoryCodes: [CategoryCode.C1],
        age: this.ageSubject.value,
        smoker: this.smokerSubject.value,
        sex: this.sexSubject.value,
        termCoverageDuration: duration ?? Duration.T10,
        defaultShownDurations: DEFAULT_SHOWN_RENEWAL_DURATIONS,
        hasWholeLife,
      },
    });
  }

  private onLifeInsuranceProductSelected(coverageAmount: number, duration: Duration) {
    const currentCoverage: Coverage = cloneDeep(this.coverageSubject.value) ?? {lifeInsurances: {}};
    const selectedDurationType = getPersonInsuranceTypeFromDuration(duration);

    // Get the duration from the current coverage that has the same insurance type as the updated duration
    const currentDurationForType = currentCoverage?.lifeInsurances
      ? Object.keys(currentCoverage.lifeInsurances).find(
          (key) => getPersonInsuranceTypeFromDuration(key as Duration) === selectedDurationType,
        )
      : undefined;

    if (currentDurationForType) {
      delete currentCoverage!.lifeInsurances[currentDurationForType];
    }

    currentCoverage.lifeInsurances[duration] = coverageAmount;

    this.coverageSubject.next(currentCoverage);
  }

  private onRiderAltered(type: RiderType, coverageAmount: number, duration?: number) {
    const currentCoverage = cloneDeep(this.coverageSubject.value);
    if (!currentCoverage) {
      throw new Error('Must have defined coverage before altering riders');
    }

    // If no coverage is provided, remove the rider from the coverage
    if (!coverageAmount) {
      currentCoverage.riders = currentCoverage.riders?.filter((rider) => rider.type !== type);
      this.coverageSubject.next(currentCoverage);
      return;
    }

    // Duration is required before a disability rider can be added to the coverage
    if (type === RiderType.DISABILITY && !duration) {
      return;
    }

    const currentMatchingRider = currentCoverage.riders?.find((rider) => rider.type === type);
    if (currentMatchingRider) {
      currentMatchingRider.coverage = coverageAmount;
      currentMatchingRider.duration = duration;
    } else {
      const newRider = {type, coverage: coverageAmount, duration};
      currentCoverage.riders = currentCoverage.riders ? [...currentCoverage.riders, newRider] : [newRider];
    }

    this.coverageSubject.next(currentCoverage);
  }

  private onCoverageAmountAltered(coverageAmount: number, type: PersonInsuranceType) {
    switch (type) {
      case 'WHOLE':
        this.wholeLifeCoverageAmountSubject.next(coverageAmount);
        break;
      case 'TERM':
        this.termLifeCoverageAmountSubject.next(coverageAmount);
        break;
    }

    if (!coverageAmount) {
      const coverage = cloneDeep(this.coverageSubject.value);
      const matchingDuration = (Object.keys(coverage?.lifeInsurances ?? {}) as Duration[]).find(
        (key) => getPersonInsuranceTypeFromDuration(key) === type,
      );
      if (matchingDuration) {
        delete coverage!.lifeInsurances[matchingDuration];
        this.coverageSubject.next(coverage);
      }
    }
  }

  private setCoverageAmountOptionsListeners() {
    // When term life coverage amount changes, update whole life coverage amount options
    this.termLifeCoverageAmountSubject
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((termLifeCoverageAmount) =>
        this.wholeLifeCoverageAmountOptionsSubject.next(
          this.protectionService.getCoverageDropdownOptions(
            HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
            HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
            HUMANIA_EMMA_V2_MAX_COVERAGE - termLifeCoverageAmount,
            false,
          ) as Dropdown<number>[],
        ),
      );

    // When whole life coverage amount or age changes, update term life coverage amount options
    combineLatest([
      this.wholeLifeCoverageAmountSubject.pipe(distinctUntilChanged()),
      this.ageSubject.pipe(distinctUntilChanged()),
    ])
      .pipe(
        map(([wholeLifeCoverageAmount, age]) => {
          if (age && age <= HUMANIA_EMMA_V2_MAX_TERM_AGE) {
            return this.protectionService.getCoverageDropdownOptions(
              HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
              HUMANIA_EMMA_V2_COVERAGE_AMOUNT_INTERVAL,
              HUMANIA_EMMA_V2_MAX_COVERAGE - wholeLifeCoverageAmount,
              false,
            ) as Dropdown<number>[];
          } else {
            return [];
          }
        }),
        untilDestroyed(this),
      )
      .subscribe((values) => this.termLifeCoverageAmountOptionsSubject.next(values));

    // When total coverage changes, update child rider coverage amount options
    this.totalCoverage$.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((totalCoverage) => {
      if (!totalCoverage) {
        this.childRiderCoverageAmountOptionsSubject.next([]);
        return;
      }

      this.childRiderCoverageAmountOptionsSubject.next(
        this.defaultChildRiderCoverageOptions.filter((option) => totalCoverage >= 2 * option.value),
      );
    });

    // When age, total coverage, or surcharge rate changes, update cancer rider coverage amount options
    combineLatest([
      this.ageSubject.pipe(distinctUntilChanged()),
      this.totalCoverage$.pipe(distinctUntilChanged()),
      this.surchargeRateSubject.pipe(distinctUntilChanged()),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([age, totalCoverage, surchargeRate]) => {
        if (!totalCoverage || surchargeRate || (age ?? 0) > HUMANIA_EMMA_V2_CANCER_RIDER_MAX_AGE) {
          this.cancerRiderCoverageAmountOptionsSubject.next([]);
          return;
        }

        this.cancerRiderCoverageAmountOptionsSubject.next(
          this.protectionService.getCoverageDropdownOptions(
            HUMANIA_EMMA_V2_CANCER_RIDER_MIN_COVERAGE,
            HUMANIA_EMMA_V2_CANCER_RIDER_COVERAGE_AMOUNT_INTERVAL_EMMA_PRO,
            HUMANIA_EMMA_V2_CANCER_RIDER_MAX_COVERAGE,
            false,
          ) as Dropdown<number>[],
        );
      });

    // When age, job risk class, surcharge rate, or total coverage changes, update disability rider coverage amount options
    combineLatest([
      this.ageSubject.pipe(distinctUntilChanged()),
      this.jobRiskClassSubject.pipe(distinctUntilChanged()),
      this.surchargeRateSubject.pipe(distinctUntilChanged()),
      this.totalCoverage$.pipe(distinctUntilChanged()),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([age, jobRiskClass, surchargeRate, totalCoverage]) => {
        if (
          !jobRiskClass ||
          jobRiskClass === JobRiskClass.INELIGIBLE ||
          !totalCoverage ||
          (age ?? 0) > HUMANIA_EMMA_V2_DISABILITY_RIDER_MAX_AGE ||
          surchargeRate
        ) {
          this.disabilityRiderCoverageAmountOptionsSubject.next([]);
          return;
        }

        const maxCoverage = getDisabilityRiderMaxCoverage(totalCoverage, jobRiskClass);
        this.disabilityRiderCoverageAmountOptionsSubject.next(
          this.protectionService.getCoverageDropdownOptions(
            HUMANIA_EMMA_V2_DISABILITY_RIDER_MIN_COVERAGE,
            HUMANIA_EMMA_V2_DISABILITY_RIDER_COVERAGE_AMOUNT_INTERVAL,
            maxCoverage,
            false,
          ) as Dropdown<number>[],
        );
      });
  }

  private setDurationOptionsListeners(): void {
    // When age, term life coverage amount, surcharge rate, sex, or smoker changes, update term life duration options
    combineLatest([
      this.ageSubject.pipe(distinctUntilChanged()),
      this.termLifeCoverageAmountSubject.pipe(distinctUntilChanged()),
      this.surchargeRateSubject.pipe(distinctUntilChanged()),
      this.sexSubject.pipe(distinctUntilChanged()),
      this.smokerSubject.pipe(distinctUntilChanged()),
      this.categoryCodeSubject.pipe(distinctUntilChanged()),
    ])
      .pipe(
        switchMap(([_, coverageAmount]) =>
          this.getPricedDurationOptionsForDurationsAndCoverageAmount(
            HUMANIA_EMMA_V2_TERM_LIFE_DURATIONS,
            coverageAmount!,
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((durationOptions) => this.termLifeDurationOptionsSubject.next(durationOptions));

    // When age, category code, whole life coverage amount, surcharge amount, sex, or smoker changes, update whole life duration options
    combineLatest([
      this.ageSubject.pipe(distinctUntilChanged()),
      this.wholeLifeCoverageAmountSubject.pipe(distinctUntilChanged()),
      this.surchargeRateSubject.pipe(distinctUntilChanged()),
      this.sexSubject.pipe(distinctUntilChanged()),
      this.smokerSubject.pipe(distinctUntilChanged()),
      this.categoryCodeSubject.pipe(distinctUntilChanged()),
    ])
      .pipe(
        mergeMap(([_, coverageAmount]) =>
          this.getPricedDurationOptionsForDurationsAndCoverageAmount(
            HUMANIA_EMMA_V2_WHOLE_LIFE_DURATIONS,
            coverageAmount!,
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((durationOptions) => this.wholeLifeDurationOptionsSubject.next(durationOptions));

    // When job risk class changes, update disability rider duration options
    this.jobRiskClassSubject.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((jobRiskClass) => {
      if (!jobRiskClass) {
        this.disabilityRiderDurationOptionsSubject.next([]);
        return;
      }

      this.disabilityRiderDurationOptionsSubject.next(
        getDisabilityRiderDurations(jobRiskClass).map((duration) => ({
          name: this.isFr ? `${duration} ans` : `${duration}-year`,
          value: duration,
        })),
      );
    });
  }

  private mapProtectionsToPricedDurationOptions(protections: Protection[]): Dropdown<Duration>[] {
    return protections.map((protection) => {
      const personInsurance = protection.personInsurances[0];
      return {
        name: `${personInsurance.duration} - ${this.currencyPipe.transform(
          personInsurance.priceBreakdown.basePremium + personInsurance.priceBreakdown.surchargeAmount,
          'CAD',
          'symbol-narrow',
          '1.2-2',
        )}`,
        value: personInsurance.duration,
      };
    });
  }

  private getPricedDurationOptionsForDurationsAndCoverageAmount(
    durations: Duration[],
    coverageAmount: number,
  ): Observable<Dropdown<Duration>[]> {
    return this.getAllPossibleProtectionsForDurationsAndCoverageAmount(durations, coverageAmount).pipe(
      map((protections) => this.mapProtectionsToPricedDurationOptions(protections)),
    );
  }

  private getAllPossibleProtectionsForDurationsAndCoverageAmount(
    durations: Duration[],
    coverageAmount: number,
  ): Observable<Protection[]> {
    if (
      !coverageAmount ||
      !this.ageSubject.value ||
      !this.categoryCodeSubject.value ||
      this.smokerSubject.value == null ||
      !this.sexSubject.value
    ) {
      return of([]);
    }

    const coverages = durations.map((duration) => ({
      lifeInsurances: {[duration]: coverageAmount},
    }));
    return this.protectionService.getProtections(
      this.ratesVersion,
      coverages,
      this.providerId,
      [this.categoryCodeSubject.value],
      this.ageSubject.value,
      this.smokerSubject.value,
      this.sexSubject.value,
      {surchargeRate: this.surchargeRateSubject.value || undefined, allowPartialSuccess: true},
    );
  }

  private setProtectionBuilderListener(): void {
    combineLatest([
      this.coverageSubject.pipe(distinctUntilChanged()),
      this.categoryCodeSubject.pipe(distinctUntilChanged()),
      this.ageSubject.pipe(distinctUntilChanged()),
      this.smokerSubject.pipe(distinctUntilChanged()),
      this.sexSubject.pipe(distinctUntilChanged()),
      this.surchargeRateSubject.pipe(distinctUntilChanged()),
      this.jobRiskClassSubject.pipe(distinctUntilChanged()),
    ])
      .pipe(
        tap(() => (this.isPriceLoading = true)),
        debounceTime(500),
        filter(
          ([coverage, categoryCode, age, smoker, sex]) =>
            coverage !== undefined && !!categoryCode && !!age && smoker != null && !!sex,
        ),
        switchMap(([coverage, categoryCode, age, smoker, sex, surchargeRate, jobRiskClass]) => {
          // Undefined coverages are not filtered out (unlike the other params) so that a value for a protection builder
          // that has been fully reset can be emitted
          if (!coverage || !Object.entries(coverage.lifeInsurances).length) {
            return of([]);
          }

          const cleanedCoverage = this.removeUnavailableCoveragesInPlace(cloneDeep(coverage));
          if (!deepEqual(cleanedCoverage, coverage)) {
            this.coverageSubject.next(cleanedCoverage);
            return EMPTY;
          }

          return this.protectionService
            .getProtections(
              this.ratesVersion,
              [coverage],
              this.providerId,
              [categoryCode!],
              age!,
              smoker!,
              sex!,
              {
                surchargeRate: surchargeRate || undefined,
                jobRiskClass: jobRiskClass ?? undefined,
              },
            )
            .pipe(catchError(() => of([])));
        }),
        untilDestroyed(this),
      )
      .subscribe((protections) => {
        this.isPriceLoading = false;

        const builtProtection = protections[0];
        this.builtProtectionSubject.next(builtProtection);
        this.protectionBuilt.emit(builtProtection);
        if (!builtProtection) {
          this.isPrefillEnabled = true;
        }
      });
  }

  private setPrefilledCoverageListener(): void {
    this.prefilledCoverageSubject
      .pipe(
        switchMap((coverage) => {
          return forkJoin([
            this.ageSubject.pipe(
              filter((age) => !!age),
              first(),
            ),
            this.sexSubject.pipe(
              filter((sex) => !!sex),
              first(),
            ),
            this.smokerSubject.pipe(
              filter((smoker) => smoker != null),
              first(),
            ),
            this.categoryCodeSubject.pipe(
              filter((categoryCode) => !!categoryCode),
              first(),
            ),
            of(coverage),
          ]);
        }),
        switchMap(([age, sex, smoker, categoryCode, coverage]) => {
          const termDuration = Object.keys(coverage.lifeInsurances).find(
            (duration) => getPersonInsuranceTypeFromDuration(duration) === 'TERM',
          );
          const wholeDuration = Object.keys(coverage.lifeInsurances).find(
            (duration) => getPersonInsuranceTypeFromDuration(duration) === 'WHOLE',
          );

          return forkJoin([
            termDuration
              ? this.getPricedDurationOptionsForDurationsAndCoverageAmount(
                  HUMANIA_EMMA_V2_TERM_LIFE_DURATIONS,
                  coverage.lifeInsurances[termDuration],
                )
              : of(undefined),
            wholeDuration
              ? this.getPricedDurationOptionsForDurationsAndCoverageAmount(
                  HUMANIA_EMMA_V2_WHOLE_LIFE_DURATIONS,
                  coverage.lifeInsurances[wholeDuration],
                )
              : of(undefined),
            of(coverage),
            of(termDuration ? coverage.lifeInsurances[termDuration] : undefined),
            of(wholeDuration ? coverage.lifeInsurances[wholeDuration] : undefined),
          ]);
        }),
        untilDestroyed(this),
      )
      .subscribe(([termDurations, wholeDurations, coverage, termCoverageAmount, wholeCoverageAmount]) => {
        if (termDurations) {
          this.termLifeDurationOptionsSubject.next(termDurations);
        }
        if (wholeDurations) {
          this.wholeLifeDurationOptionsSubject.next(wholeDurations);
        }
        if (termCoverageAmount) {
          this.termLifeCoverageAmountSubject.next(termCoverageAmount);
        }
        if (wholeCoverageAmount) {
          this.wholeLifeCoverageAmountSubject.next(wholeCoverageAmount);
        }
        this.coverageSubject.next(coverage);
      });
  }

  /**
   * Uses the coverage amount options and duration options to determine which coverage amounts/durations are available.
   * If a coverage amount/duration is not available, it is removed from the coverage. This function works based on the
   * assumption that it runs after the options have been updated. This assumption is supported by the use of a debounce
   * time on the protection builder listener.
   * @param coverage The coverage object to modify.
   * @returns The modified coverage object.
   */
  private removeUnavailableCoveragesInPlace(coverage: Coverage): Coverage {
    Object.entries(coverage.lifeInsurances).forEach(([duration, coverageAmount]) => {
      const options = this.PRODUCT_TYPE_TO_OPTIONS[getPersonInsuranceTypeFromDuration(duration)];
      const isCoverageAmountAvailable = options?.coverageOptions.value.some(
        (option) => option.value === coverageAmount,
      );
      const isDurationAvailable = options?.durationOptions?.value.some((option) => option.value === duration);

      if (!isCoverageAmountAvailable || !isDurationAvailable) {
        delete coverage.lifeInsurances[duration];
      }
    });

    coverage.riders?.forEach((rider) => {
      const options = this.PRODUCT_TYPE_TO_OPTIONS[rider.type];
      const isCoverageAmountAvailable = options?.coverageOptions.value.some(
        (option) => option.value === rider.coverage,
      );
      const isDurationAvailable = options?.durationOptions?.value.some(
        (option) => option.value === rider.duration,
      );

      if (!isCoverageAmountAvailable || (rider.duration && !isDurationAvailable)) {
        coverage.riders = coverage.riders?.filter((r) => r.type !== rider.type);
      }
    });

    return coverage;
  }
}
